import {createContext, ReactNode, useContext, useEffect, useRef, useState} from "react"
import axios, {AxiosError} from "axios"
import {ICart, ICartItem} from "../models/ICart.ts"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import AuthContext from "./AuthContext.tsx";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

interface ICartContextProps {
    cart: ICart | undefined
    cartIsLoading: boolean
    errorLoadingCart: any
    initCartError: any
    addCartItemMutation: any
    addCartItemError: AxiosError | undefined
    removeCartItemsMutation: any
    removeCartItemError: AxiosError | undefined
    reserveCartItemsMutation: any
    reserveCartItemsError: AxiosError | undefined
    cancelReservationMutation: any
    cancelReservationError: AxiosError | undefined
    cartItemCount: number
}

const CartContext = createContext<ICartContextProps>({} as ICartContextProps
)

interface ICartProviderProps {
    children: ReactNode;
}

export const CartProvider = ({children}: ICartProviderProps) => {
    const retryCount = useRef(0);
    const maxRetries = 3; // Set your retry limit
    const {isLoggedIn} = useContext(AuthContext)
    const queryClient = useQueryClient()
    const [cart, setCart] = useState<ICart>()
    const [initCartError, setInitCartError] = useState<any>()
    const [addCartItemError, setAddCartItemError] = useState<AxiosError>()
    const [removeCartItemError, setRemoveCartItemError] = useState<AxiosError>()
    const [reserveCartItemsError, setReserveCartItemsError] = useState<AxiosError>()
    const [cancelReservationError, setCancelReservationError] = useState<AxiosError>()
    const [cartItemCount, setCartItemCount] = useState<number>(0)

    const {data: cartData, isLoading: cartIsLoading, error: errorLoadingCart} = useQuery({
        queryKey: ['cart'],
        retry: false,
        queryFn: async () => {
            const endpoint = isLoggedIn
                ? `${VITE_API_BASE_URL}/cart`
                : `${VITE_API_BASE_URL}/cart/session`

            const cartResponse = await axios.get(endpoint, {withCredentials: true})
            const cart = cartResponse.data
            if (!isLoggedIn && cart.items) {
                const productIds = cart.items.map((item: any) => item.product)

                const productPromises = productIds.map((id: any) =>
                    axios.get(`${VITE_API_BASE_URL}/products/${id}`, {withCredentials: true})
                )

                const productResponses = await Promise.all(productPromises)

                const enrichedItems = cart.items.map((item: any) => {
                    const product = productResponses.find((res) => res.data._id === item.product)?.data
                    return {...item, product}
                })

                return {...cart, items: enrichedItems}
            }

            return cart;
        },
        enabled: isLoggedIn !== undefined,
    })

    const initCartMutation = useMutation({
        mutationFn: async () => {
            if (isLoggedIn)
                await axios.post(`${VITE_API_BASE_URL}/cart/init`, {}, {withCredentials: true})
            else
                await axios.post(`${VITE_API_BASE_URL}/cart/session/init`, {}, {withCredentials: true})
        },
        onSuccess: () => queryClient.invalidateQueries({queryKey: ['cart']}),
        onError: (error: AxiosError) => setInitCartError(error),
    });

    const addCartItemMutation = useMutation({
        mutationFn: async (productId: string) => {
            if (isLoggedIn)
                await axios.post(`${VITE_API_BASE_URL}/cart/add`, {
                    productId: productId,
                    quantity: 1
                }, {withCredentials: true})
            else
                await axios.post(`${VITE_API_BASE_URL}/cart/session/add`, {
                    productId: productId,
                    quantity: 1
                }, {withCredentials: true})
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['cart']})
            setCartItemCount(cartItemCount + 1)
        },
        onError: (error: AxiosError) => setAddCartItemError(error)
    })

    const removeCartItemsMutation = useMutation({
        mutationFn: async (cartItem: ICartItem) => {
            if (isLoggedIn) {
                await axios.post(`${VITE_API_BASE_URL}/cart/remove`, {productId: cartItem.product._id}, {withCredentials: true})
                return cartItem.quantity
            } else {
                await axios.post(`${VITE_API_BASE_URL}/cart/session/remove`, {productId: cartItem.product._id}, {withCredentials: true})
                return cartItem.quantity
            }
        },
        onSuccess: (quantity) => {
            setCartItemCount(cartItemCount - quantity)
            queryClient.invalidateQueries({queryKey: ['cart']})
        },
        onError: (error: AxiosError) => setRemoveCartItemError(error)
    })

    const reserveCartItemsMutation = useMutation({
        mutationFn: async () => {
            if (isLoggedIn)
                await axios.post(`${VITE_API_BASE_URL}/cart/reserve-products`, {}, {withCredentials: true})
            else
                await axios.post(`${VITE_API_BASE_URL}/cart/session/reserve-products`, {}, {withCredentials: true})
        },
        onError: (error: AxiosError) => setReserveCartItemsError(error)
    })


    const cancelReservationMutation = useMutation({
        mutationFn: async () => {
            if (isLoggedIn)
                await axios.post(`${VITE_API_BASE_URL}/cart/release-reservation`, {}, {withCredentials: true})
            else
                await axios.post(`${VITE_API_BASE_URL}/cart/session/release-reservation`, {}, {withCredentials: true})
        },
        onError: (error: AxiosError) => setCancelReservationError(error)
    })

    useEffect(() => {
        if (errorLoadingCart) {
            const error = errorLoadingCart as AxiosError;
            if (error.status === 404 && retryCount.current < maxRetries) {
                retryCount.current += 1; // Increment retry counter
                initCartMutation.mutate();
            }
        } else {
            setCart(cartData as ICart);
        }
    }, [cartData, errorLoadingCart]);

    useEffect(() => {
        if (cart) {
            let count = 0
            cart?.items.forEach((item: any) => {
                count += item.quantity
            })
            setCartItemCount(count)
        }
    }, [cart]);

    return (
        <CartContext.Provider
            value={{
                cart,
                cartIsLoading,
                errorLoadingCart,
                initCartError,
                addCartItemMutation,
                addCartItemError,
                removeCartItemsMutation,
                removeCartItemError,
                reserveCartItemsMutation,
                reserveCartItemsError,
                cancelReservationMutation,
                cancelReservationError,
                cartItemCount,
            }}>
            {children}
        </CartContext.Provider>
    )
}

export default CartContext